import { useCallback, useEffect } from 'react';
import { useAuth } from '@bymason-private/auth-provider';
import { useGetAccount } from '@bymason/controller-api-hooks';
import { ControllerFontType } from '@bymason-private/controller-v1-theme-provider';
import { Box, Button, Divider, Text } from '@bymason-private/legacy-pattern-components';
import { useToast, LegacyDetailsList } from '@bymason-private/controller-shared-widgets';
import Layout from '../../common/layout';

export default function AccountView() {
  const { authToken = '', resetPassword, userInfo } = useAuth();
  const [getAccount, account, accountError, accountBusy] = useGetAccount(authToken);
  const [setToastMessage, setToastSeverity] = useToast();

  const handleResetPassword = useCallback(async () => {
    if (resetPassword) {
      try {
        await resetPassword();
        setToastMessage(`A password reset email has been sent to ${userInfo?.email || 'unknown'}`);
        setToastSeverity('success');
      } catch (error) {
        setToastMessage(String(error));
        setToastSeverity('error');
      }
    }
  }, [resetPassword, setToastMessage, setToastSeverity, userInfo?.email]);

  //
  // handle retrieving account info
  //
  useEffect(() => {
    getAccount({});
  }, [getAccount]);

  //
  // handle errors
  //
  useEffect(() => {
    if (!accountBusy && accountError) {
      setToastMessage(accountError);
      setToastSeverity('error');
    }
  }, [accountBusy, accountError, setToastMessage, setToastSeverity]);

  return (
    <Layout
      busy={accountBusy}
      headerRightAdornment={
        <Box
          sx={{ height: 32 /* this is so header height is same as other views that have action buttons */ }}
        />
      }
      headerText='Account'
    >
      <LegacyDetailsList
        addColonToLabel
        hideIconsColumn
        items={[
          { label: 'Name', value: userInfo?.name ?? '' },
          { label: 'Email', value: userInfo?.email ?? '' },
          { label: 'Organization', value: account ? account.companyName : '' },
        ]}
        labelWidth={120}
      />
      <Divider sx={{ mb: 2, mt: 3 }} />
      <Text variant={ControllerFontType.Heading300B}>Change password</Text>
      <Text paragraph variant={ControllerFontType.Body400R} sx={{ mt: 1 }}>
        An email will be sent to you with a link to change your password
      </Text>
      <Button disabled={!resetPassword} onClick={handleResetPassword} variant='outlined'>
        Request password change
      </Button>
    </Layout>
  );
}
